import { useCallback, useState } from "react";
import { Button, Card } from "./ui";
import { api, ApiError } from "../lib/api";
import { USAGE_PACKS, PACK_VAT_NOTE } from "../lib/pack-config";

/**
 * "Buy more BALDER builds" — one-time usage packs (paid plans only). Each pack starts a
 * hosted one-time checkout server-side and redirects to the provider's secure page. No
 * card data is handled here; the credit is applied by the billing webhook once payment
 * confirms, and appears against the BALDER allowance on this page shortly after.
 */
export function UsagePacks({ tenantId }: { tenantId: string }): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buy = useCallback(
    async (packCode: string) => {
      setBusy(packCode);
      setError(null);
      try {
        const result = await api.billing.startPackCheckout(tenantId, { packCode });
        window.location.assign(result.checkoutUrl);
      } catch (err) {
        if (err instanceof ApiError && err.code === "validation") {
          setError(err.message);
        } else {
          setError("Could not start checkout. Please try again shortly.");
        }
        setBusy(null);
      }
    },
    [tenantId],
  );

  return (
    <Card title="Need more BALDER builds?">
      <p className="muted">
        Top up your current billing period with extra BALDER build requests. Packs are a one-time payment,
        added to your allowance as soon as payment confirms — no change to your plan.
      </p>
      {error ? (
        <div className="banner-item banner-item--warn" role="status" style={{ marginBottom: "0.75rem" }}>
          <div className="banner-item__message">
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        </div>
      ) : null}
      <div className="card-grid">
        {USAGE_PACKS.map((pack) => (
          <div key={pack.code} className="kv-grid" style={{ display: "grid", gap: "0.4rem", padding: "0.75rem", border: "1px solid var(--border, #333)", borderRadius: "0.5rem" }}>
            <strong>{pack.name}</strong>
            <span className="kv__v">
              {pack.price} <small className="muted">VAT included</small>
            </span>
            <Button variant="secondary" onClick={() => void buy(pack.code)} disabled={busy !== null}>
              {busy === pack.code ? "Starting checkout…" : `Buy ${pack.price}`}
            </Button>
          </div>
        ))}
      </div>
      <p className="muted" style={{ marginTop: "0.75rem" }}>{PACK_VAT_NOTE}</p>
      <p className="muted">
        Payment is handled entirely by our payment provider on their secure hosted page. Card details are
        never entered into or stored by this dashboard.
      </p>
    </Card>
  );
}
